import { Divider } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import DeleteOutlineOutlinedIcon from '@material-ui/icons/DeleteOutlineOutlined';
import SettingsBackupRestoreOutlinedIcon from '@material-ui/icons/SettingsBackupRestoreOutlined';
import { TextLinkButton } from 'framework/components/buttons/TextLinkButton';
import { CaptionVerticalLabelledPropertyX } from 'framework/components/labelledProperty/CaptionVerticalLabelledPropertyX';
import { TypographyBoldCaption } from 'framework/components/typography/TypographyBoldCaption';
import { AlertDialog } from 'framework/dialogs/AlertDialog';
import {
	generalPractitionersCommand_addOffice,
	generalPractitionersCommand_delete,
	generalPractitionersCommand_restore,
	IGeneralPractitioner,
} from 'gen/ApiClient';
import { useLocalization } from 'localization/useLocalization';
import React from 'react';
import { useDialogsContext } from 'shared/dialogs/useDialogsContext';
import { OfficeForm } from 'shared/forms/OfficeForm';
import { useViewDocumentation } from 'shared/utils/useViewDocumentation';
import { PatchGeneralPractitionerPersonalInformationForm } from './forms/PatchGeneralPractitionerPersonalInformationForm';
import { PatchGeneralPractitionerRizivNumberForm } from './forms/PatchGeneralPractitionerRizivNumberForm';
import { GeneralPractitionerOfficeListItem } from './GeneralPractitionerOfficeListItem';

interface IProps {
	item: IGeneralPractitioner;
	reload: VoidFunction;
	style?: React.CSSProperties;
}

export const GeneralPractitionerDetailsContent = ({ item, reload, style }: IProps) => {
	const strings = useLocalization();
	const { open, confirm, cancel } = useDialogsContext(reload);
	const onViewRizivNumberingInfo = useViewDocumentation('riziv-numbering-info');

	const onPatchDetails = () => {
		open(
			<PatchGeneralPractitionerPersonalInformationForm
				open
				generalPractitioner={item}
				confirm={confirm}
				cancel={cancel}
			/>
		);
	};

	const onPatchRizivNumber = () => {
		open(
			<PatchGeneralPractitionerRizivNumberForm
				open
				confirm={confirm}
				cancel={cancel}
				generalPractitioner={item}
			/>
		);
	};

	const onAddOffice = () => {
		open(
			<OfficeForm
				open
				submitFunction={model => generalPractitionersCommand_addOffice({ generalPractitionerId: item.id, office: model })}
				confirm={confirm}
				cancel={cancel}
			/>
		);
	};

	const onDelete = () => {
		open(
			<AlertDialog
				open
				content={strings.deleteWhatQuestion(strings.generalPractitioner)}
				acceptText={strings.yesCommaDelete}
				rejectText={strings.noCommaGoBack}
				reject={cancel}
				acceptF={() => generalPractitionersCommand_delete({ generalPractitionerId: item.id })}
				accept={confirm}
			/>
		);
	};

	const onRestore = () => {
		open(
			<AlertDialog
				open
				title={strings.undoDelete}
				content={strings.doYouWantToMakeThisGPAvailableAgainQuestion}
				acceptText={strings.yesCommaContinue}
				rejectText={strings.cancel}
				reject={cancel}
				acceptF={() => generalPractitionersCommand_restore({ generalPractitionerId: item.id })}
				accept={confirm}
			/>
		);
	};

	return (
		<div
			className='df-col gap-8'
			style={style}>
			<CaptionVerticalLabelledPropertyX
				label={strings.name}
				edit={onPatchDetails}>
				{item.identifier}
			</CaptionVerticalLabelledPropertyX>
			<CaptionVerticalLabelledPropertyX
				label={strings.RIZIVNumber}
				edit={onPatchRizivNumber}
				info={onViewRizivNumberingInfo}>
				{item.conventionedRizivNumber}
			</CaptionVerticalLabelledPropertyX>
			{item.hasOffices && (
				<>
					<TypographyBoldCaption style={{ marginBottom: 0 }}>{strings.locations}</TypographyBoldCaption>
					{item.offices.map((t, index) => (
						<GeneralPractitionerOfficeListItem
							key={t.id}
							item={t}
							index={index + 1}
							generalPractitioner={item}
							reload={reload}
						/>
					))}
				</>
			)}
			<div className='fg1'></div>
			<Divider style={{ marginBottom: 4 }} />
			<div className='df-col gap-8'>
				<TextLinkButton
					startIcon={<AddIcon />}
					onClick={onAddOffice}>
					{strings.addLocation}
				</TextLinkButton>
				{item.isDeleted === false && (
					<TextLinkButton
						startIcon={<DeleteOutlineOutlinedIcon />}
						hoverColor='secondary'
						onClick={onDelete}>
						{strings.delete}
					</TextLinkButton>
				)}
				{item.isDeleted && (
					<TextLinkButton
						startIcon={<SettingsBackupRestoreOutlinedIcon />}
						color='secondary'
						hoverColor='secondary'
						onClick={onRestore}>
						{strings.restoreVerb}
					</TextLinkButton>
				)}
			</div>
		</div>
	);
};
